'use client';

import {cloneEventWithNewValue} from '@rocket-ui/tools';
import React, {useCallback, useMemo, useRef} from 'react';
import {conformToMask, Mask} from 'react-text-mask';
import {Input} from './Input';
import {InputProps} from './types';

type InputMask = Mask | ((rawValue: string) => Mask);
type InputValue = MaskedInputProps['value'];

export type MaskedInputProps = InputProps<string> & {
	mask: InputMask;
};

export const MaskedInput = React.forwardRef<HTMLInputElement, MaskedInputProps>(function MaskedInput(
	{value: inputValue, onChange, mask, ...props},
	ref,
) {
	const previousConformedValue = useRef<string>(String(inputValue));

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = formatUsingMask<string>(mask, event.target.value, previousConformedValue.current);

			onChange?.(cloneEventWithNewValue(event, value));

			previousConformedValue.current = value;
		},
		[mask, onChange],
	);

	const value = useMemo(() => formatUsingMask(mask, inputValue, previousConformedValue.current), [mask, inputValue]);

	return <Input ref={ref} value={value} onChange={handleChange} {...props} />;
});

function formatUsingMask<T extends InputValue>(mask: InputMask, value: T, fallbackValue: T) {
	if (Array.isArray(mask) && !mask.length) return value;

	const {conformedValue: maskedValue} = conformToMask(String(value ?? ''), mask, {
		guide: false,
		previousConformedValue: fallbackValue,
	});

	if (!maskedValue && typeof mask !== 'boolean') {
		const _mask = Array.isArray(mask) ? mask : (mask(maskedValue) as Array<string | RegExp>);
		const index = _mask.findIndex((item) => typeof item !== 'string');

		return _mask.slice(0, index).join('') as T;
	}

	return maskedValue as T;
}
